/**
 * CatchMoments 큐브 아이콘 렌더러 — 3×3 큐브(윗면 C · 왼쪽 면 M)를 1024 좌표계에 그리고, 모션별로
 * 시각 t(초)의 그리기 옵션을 낸다. DOM 에 기대지 않으므로 브라우저 캔버스와 node-canvas 가 함께 쓴다.
 *
 * 🔑 좌표·색·타이밍이 곧 브랜드 아이콘 정본이다 — 숫자를 다듬지 마라.
 */

export type BrandCubeAnim = 'static' | 'solve' | 'gather' | 'assemble' | 'spin' | 'float' | 'shimmer' | 'loader';

export type Ctx2D = CanvasRenderingContext2D;

export type BrandCubeAnimSpec = {
  /** 한 루프 길이(초) */
  T: number;
  /** 모션 줄이기·첫 화면에 쓰는 정지 시각(초) */
  rest: number;
  f: (t: number) => DrawOpts;
};

export type DrawOpts = {
  cube: CubeState;
  alpha?: number;
  zoom?: number;
  lift?: number;
  shadow?: number;
  glow?: (st: Sticker) => number;
  scale?: (st: Sticker) => number;
  xf?: (c: Cubie) => { off: Vec; R: Mat } | null;
};

export type RenderOpts = {
  /** 둥근 사각 바탕(앱 아이콘)을 깐다 */
  bg: boolean;
  /** 어두운 바탕 위 — 그림자를 진하게 */
  dark: boolean;
  dpr: number;
  /** 반투명 프레임용 임시 캔버스 — 없으면 alpha 를 무시한다 */
  offscreen?: () => { getContext: (k: '2d') => Ctx2D | null; width: number; height: number };
  /** 큐브 확대 배율 — 작은 칸에서 큐브를 칸에 꽉 채울 때(기본 1) */
  fit?: number;
  /** 빛(glow) 세기 배율 — 작은 칸에서 빛이 묻힐 때(기본 1, 불투명도 0.9 상한) */
  glowGain?: number;
};

/**
 * 모션 하나의 시각 t 장면을 그린다.
 * @param px 캔버스 한 변의 CSS 픽셀(캔버스 실제 크기는 px × dpr)
 */
export function renderBrandCube(ctx: Ctx2D, px: number, anim: BrandCubeAnim, t: number, o: RenderOpts) {
  const scene = ANIM[anim].f(t);
  const { fit = 1, glowGain = 1 } = o;
  const glow = scene.glow && glowGain !== 1 ? (st: Sticker) => Math.min(0.9, scene.glow!(st) * glowGain) : scene.glow;
  draw(ctx, px, { ...scene, ...o, glow, zoom: (scene.zoom ?? 1) * fit });
}

export function brandCubeSpec(anim: BrandCubeAnim): BrandCubeAnimSpec {
  return ANIM[anim];
}

// ----------------------------------------------------------------------

type Vec = [number, number, number];
type Mat = [Vec, Vec, Vec];
type Pt = [number, number];

type Sticker = { u: Vec; v: Vec; fill: string; tri: string | null; seq: number; base: string | null };
type Cubie = { h: Vec; M: Mat; faces: { d: Vec; st: Sticker | null }[] };
type CubeState = { cs: Cubie[]; partial: { R: Mat; test: (c: Cubie) => boolean } | null };
type Move = { ax: 'x' | 'y' | 'z'; idx: number | 'all'; dir: number; dur: number; ease?: (p: number) => number };
type Step = Move | { pause: number };

const CP = ['XXX', 'X..', 'XXX'];
const MP = ['XVX', 'X.X', 'X.X'];
const GR: Record<string, [string, string]> = {
  cy: ['#B4E4F5', '#8FCFEA'],
  wh: ['#FFFFFF', '#E6E9EF'],
  st: ['#323A52', '#2A3147'],
  sl: ['#252B3E', '#1F2435'],
  sr: ['#1B2030', '#161A27'],
};
const BODY_RGB = { T: [44, 48, 57], L: [38, 42, 50], R: [32, 35, 43] };
const BODY_GR: Record<string, [string, string]> = {
  st: ['#4C5263', '#444A5A'],
  sl: ['#434959', '#3C4150'],
  sr: ['#3A3F4D', '#343846'],
};
const DIRS: Vec[] = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
const I: Mat = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
/** 스티커가 칸을 채우는 비율 */
const K = 0.86;
/** C 획 순서 (c,r) */
const C_ORDER = [[2, 0], [1, 0], [0, 0], [0, 1], [0, 2], [1, 2], [2, 2]];
/** M 획 순서 (c,r) */
const M_ORDER = [[0, 2], [0, 1], [0, 0], [1, 0], [2, 0], [2, 1], [2, 2]];
const VIEW: Vec = [1, 1, 1].map((v) => v / Math.sqrt(3)) as Vec;

const TAN = (d: Vec): [Vec, Vec] =>
  d[0] ? [[0, 0, 1], [0, 1, 0]] : d[1] ? [[1, 0, 0], [0, 0, 1]] : [[1, 0, 0], [0, 1, 0]];
const dot = (a: Vec, b: Vec) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const add = (a: Vec, b: Vec, s = 1): Vec => [a[0] + b[0] * s, a[1] + b[1] * s, a[2] + b[2] * s];
const sub = (a: Vec, b: Vec): Vec => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const rot = (ax: 'x' | 'y' | 'z', t: number): Mat => {
  const c = Math.cos(t);
  const s = Math.sin(t);
  if (ax === 'x') return [[1, 0, 0], [0, c, -s], [0, s, c]];
  if (ax === 'y') return [[c, 0, s], [0, 1, 0], [-s, 0, c]];
  return [[c, -s, 0], [s, c, 0], [0, 0, 1]];
};
const mm = (a: Mat, b: Mat): Mat =>
  a.map((r) => [0, 1, 2].map((j) => r[0] * b[0][j] + r[1] * b[1][j] + r[2] * b[2][j])) as Mat;
const mv = (m: Mat, v: Vec): Vec => [dot(m[0], v), dot(m[1], v), dot(m[2], v)];
const roundM = (m: Mat): Mat => m.map((r) => r.map(Math.round)) as Mat;
const ease = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
const easeSoft = (p: number) => {
  const c2 = 1.525;
  return p < 0.5
    ? (Math.pow(2 * p, 2) * ((c2 + 1) * 2 * p - c2)) / 2
    : (Math.pow(2 * p - 2, 2) * ((c2 + 1) * (p * 2 - 2) + c2) + 2) / 2;
};
const back = (x: number) => {
  const c = 1.70158;
  return 1 + (c + 1) * Math.pow(x - 1, 3) + c * Math.pow(x - 1, 2);
};
const clamp = (x: number) => Math.max(0, Math.min(1, x));

function sticker(h: Vec, d: Vec): Sticker | null {
  if (dot(h, d) !== 1) return null;
  if (d[1] === 1) {
    const c = h[0] + 1;
    const r = h[2] + 1;
    if (CP[r][c] === 'X') {
      return { u: [1, 0, 0], v: [0, 0, 1], fill: 'cy', tri: null, base: 'st', seq: C_ORDER.findIndex((p) => p[0] === c && p[1] === r) };
    }
    return { u: [1, 0, 0], v: [0, 0, 1], fill: 'st', tri: null, base: null, seq: -1 };
  }
  if (d[2] === 1) {
    const c = h[0] + 1;
    const r = 1 - h[1];
    const ch = MP[r][c];
    const u: Vec = [1, 0, 0];
    const v: Vec = [0, -1, 0];
    if (ch === 'X') return { u, v, fill: 'wh', tri: null, base: 'sl', seq: 7 + M_ORDER.findIndex((p) => p[0] === c && p[1] === r) };
    if (ch === 'V') return { u, v, fill: 'sl', tri: 'wh', base: null, seq: 10.5 };
    return { u, v, fill: 'sl', tri: null, base: null, seq: -1 };
  }
  const [u, v] = TAN(d);
  return { u, v, fill: 'sr', tri: null, base: null, seq: -1 };
}

function model(): Cubie[] {
  const cs: Cubie[] = [];
  for (let x = -1; x <= 1; x++)
    for (let y = -1; y <= 1; y++)
      for (let z = -1; z <= 1; z++) {
        if (!x && !y && !z) continue;
        const h: Vec = [x, y, z];
        cs.push({ h, M: I, faces: DIRS.map((d) => ({ d, st: sticker(h, d) })) });
      }
  return cs;
}

/** 회전 순서표를 시각 t 까지 적용한 큐브 — 진행 중인 회전은 partial 로 돌려준다 */
function cubeAt(seq: Step[], t: number): CubeState {
  const cs = model();
  let partial: CubeState['partial'] = null;
  let acc = 0;
  for (const s of seq) {
    if ('pause' in s) {
      acc += s.pause;
      if (t < acc) break;
      continue;
    }
    const end = acc + s.dur;
    const axis = 'xyz'.indexOf(s.ax);
    const inLayer = (c: Cubie) => s.idx === 'all' || Math.round(mv(c.M, c.h)[axis]) === s.idx;
    if (t >= end) {
      const R = roundM(rot(s.ax, (s.dir * Math.PI) / 2));
      cs.forEach((c) => {
        if (inLayer(c)) c.M = mm(R, c.M);
      });
      acc = end;
      continue;
    }
    if (t > acc) partial = { R: rot(s.ax, ((s.dir * Math.PI) / 2) * (s.ease || ease)((t - acc) / s.dur)), test: inLayer };
    break;
  }
  return { cs, partial };
}

// ---------- 그리기 ----------

const P = (p: Vec): Pt => {
  const X = 512 + p[0] * 95.333 - p[2] * 95.333;
  const Y = 520 + p[0] * 55 + p[2] * 55 - p[1] * 110;
  return [512 + (X - 512) * 0.92, 512 + (Y - 534) * 0.92];
};

function poly(ctx: Ctx2D, pts: Pt[]) {
  ctx.beginPath();
  pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
  ctx.closePath();
}

function gfill(ctx: Ctx2D, pts: Pt[], key: string) {
  let x0 = 1e9;
  let y0 = 1e9;
  let x1 = -1e9;
  let y1 = -1e9;
  pts.forEach((p) => {
    x0 = Math.min(x0, p[0]);
    y0 = Math.min(y0, p[1]);
    x1 = Math.max(x1, p[0]);
    y1 = Math.max(y1, p[1]);
  });
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  const cs = BODY_GR[key] || GR[key];
  g.addColorStop(0, cs[0]);
  g.addColorStop(1, cs[1]);
  return g;
}

function shadeBody(n: Vec) {
  const w = [Math.max(0, n[0]), Math.max(0, n[1]), Math.max(0, n[2])];
  const s = w[0] + w[1] + w[2] || 1;
  const { R, T, L } = BODY_RGB;
  const c = [0, 1, 2].map((i) => (R[i] * w[0] + T[i] * w[1] + L[i] * w[2]) / s);
  return `rgb(${c.map(Math.round).join(',')})`;
}

function bgRect(ctx: Ctx2D) {
  const r = 230;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(1024, 0, 1024, 1024, r);
  ctx.arcTo(1024, 1024, 0, 1024, r);
  ctx.arcTo(0, 1024, 0, 0, r);
  ctx.arcTo(0, 0, 1024, 0, r);
  ctx.closePath();
}

type FullOpts = DrawOpts & RenderOpts & { noShadow?: boolean };

function draw(ctx: Ctx2D, px: number, o: FullOpts) {
  const a = o.alpha ?? 1;
  if (a < 1 && o.offscreen) {
    const off = o.offscreen();
    const octx = off.getContext('2d');
    if (octx) {
      draw(octx, px, { ...o, alpha: 1, bg: false });
      draw(ctx, px, { ...o, alpha: 1, cube: { cs: [], partial: null }, noShadow: true });
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = a;
      ctx.drawImage(off as unknown as CanvasImageSource, 0, 0);
      ctx.globalAlpha = 1;
      return;
    }
  }
  const { cs, partial } = o.cube;
  const s = px / 1024;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.setTransform(s * o.dpr, 0, 0, s * o.dpr, 0, 0);
  if (o.bg) {
    bgRect(ctx);
    const g = ctx.createLinearGradient(0, 0, 307, 1024);
    g.addColorStop(0, '#FFFFFF');
    g.addColorStop(1, '#EEF0F4');
    ctx.fillStyle = g;
    ctx.fill();
    ctx.save();
    bgRect(ctx);
    ctx.clip();
  } else ctx.save();
  if (o.noShadow) {
    ctx.restore();
    return;
  }
  if (o.zoom) {
    ctx.translate(512, 512);
    ctx.scale(o.zoom, o.zoom);
    ctx.translate(-512, -512);
  }
  const sh = o.shadow ?? 1;
  ctx.save();
  ctx.translate(512, 512 + (884 - 534) * 0.92);
  ctx.scale(sh * 0.92, (sh * 0.92 * 46) / 290);
  const rg = ctx.createRadialGradient(0, 0, 0, 0, 0, 290);
  rg.addColorStop(0, o.dark ? 'rgba(0,0,0,.45)' : 'rgba(21,26,40,.25)');
  rg.addColorStop(1, 'rgba(21,26,40,0)');
  ctx.fillStyle = rg;
  ctx.beginPath();
  ctx.arc(0, 0, 290, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.translate(0, o.lift || 0);
  ctx.lineJoin = 'round';

  const items = cs
    .map((c) => {
      const W = partial && partial.test(c) ? mm(partial.R, c.M) : c.M;
      const cw = mv(W, c.h);
      const xf = o.xf ? o.xf(c) : null;
      const X = xf ? (p: Vec) => add(add(mv(xf.R, sub(p, cw)), cw), xf.off) : (p: Vec) => p;
      return { c, W, X, xf, depth: dot(xf ? add(cw, xf.off) : cw, [1, 1, 1]) };
    })
    .sort((p, q) => p.depth - q.depth);

  for (const { c, W, X, xf } of items) {
    for (const f of c.faces) {
      const n0 = mv(W, f.d);
      const n = xf ? mv(xf.R, n0) : n0;
      const vis = dot(n, VIEW);
      if (vis <= 0.02) continue;
      const fc = add(c.h, f.d, 0.5);
      const [tu, tv] = TAN(f.d);
      const body = ([[-1, -1], [1, -1], [1, 1], [-1, 1]] as const).map(([p, q]) =>
        P(X(mv(W, add(add(fc, tu, p * 0.5), tv, q * 0.5))))
      );
      ctx.fillStyle = shadeBody(n);
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = 10;
      poly(ctx, body);
      ctx.fill();
      ctx.stroke();
      const st = f.st;
      if (!st) continue;
      const corner = (p: number, q: number, sc: number) =>
        P(X(mv(W, add(add(fc, st.u, (p - 0.5) * K * sc), st.v, (q - 0.5) * K * sc))));
      const quad = (sc: number) => ([[0, 0], [1, 0], [1, 1], [0, 1]] as const).map(([p, q]) => corner(p, q, sc));
      const paint = (pts: Pt[], key: string) => {
        const g = gfill(ctx, pts, key);
        ctx.fillStyle = g;
        ctx.strokeStyle = g;
        ctx.lineWidth = 8;
        poly(ctx, pts);
        ctx.fill();
        ctx.stroke();
      };
      const sc = o.scale ? o.scale(st) : 1;
      if (st.base && sc < 1) paint(quad(1), st.base);
      if (st.tri) {
        paint(quad(1), st.fill);
        if (sc > 0.01) {
          const c0 = [0.5, 0.33];
          paint(
            ([[0, 0], [1, 0], [0.5, 1]] as const).map(([p, q]) => corner(c0[0] + (p - c0[0]) * sc, c0[1] + (q - c0[1]) * sc, 1)),
            st.tri
          );
        }
      } else if (sc > 0.01) paint(quad(sc), st.fill);
      if (vis < 0.5) {
        ctx.fillStyle = `rgba(10,13,20,${(0.5 - vis) * 0.7})`;
        poly(ctx, quad(1));
        ctx.fill();
      }
      const gl = o.glow ? o.glow(st) : 0;
      if (gl > 0) {
        ctx.fillStyle = `rgba(255,255,255,${gl})`;
        ctx.lineWidth = 8;
        ctx.strokeStyle = ctx.fillStyle;
        poly(ctx, quad(1));
        ctx.fill();
        ctx.stroke();
      }
    }
  }
  ctx.restore();
}

// ---------- 모션 ----------

const len = (seq: Step[]) => seq.reduce((acc, s) => acc + ('pause' in s ? s.pause : s.dur), 0);

const SCR3: Omit<Move, 'dur'>[] = [
  { ax: 'y', idx: 1, dir: 1 },
  { ax: 'x', idx: 1, dir: -1 },
  { ax: 'z', idx: 1, dir: 1 },
];
const solveSeq: Step[] = [...SCR3.map((m) => ({ ...m, dur: 1e-4 })), { pause: 0.55 }];
[...SCR3].reverse().forEach((m, i) => {
  solveSeq.push({ ...m, dir: -m.dir, dur: 0.78, ease: easeSoft });
  if (i < 2) solveSeq.push({ pause: 0.14 });
});
const T_SOLVED = len(solveSeq);
const T_SOLVE = T_SOLVED + 2.4;
const turnSeq: Step[] = [];
for (let i = 0; i < 4; i++) turnSeq.push({ ax: 'y', idx: 'all', dir: -1, dur: 0.8 }, { pause: 0.7 });
const loadSeq: Step[] = [];
for (let i = 0; i < 4; i++) loadSeq.push({ ax: 'y', idx: 1, dir: 1, dur: 0.45 }, { pause: 0.25 });
const SOLVED = cubeAt([], 0);

const RND = (h: Vec) => {
  const seed = (h[0] + 2) * 17 + (h[1] + 2) * 31 + (h[2] + 2) * 47;
  const r = (i: number) => {
    const x = Math.sin(seed * 12.9898 + i * 78.233) * 43758.5453;
    return (x - Math.floor(x)) * 2 - 1;
  };
  return [r(1), r(2), r(3), r(4)];
};
/** gather 에서 조각이 끼워지는 순서 — 아래층부터 */
const GORDER: Record<string, number> = {};
[...SOLVED.cs]
  .sort((p, q) => p.h[1] - q.h[1] || p.h[0] + p.h[2] - (q.h[0] + q.h[2]) || p.h[0] - q.h[0])
  .forEach((c, i) => {
    GORDER[c.h.join()] = i;
  });

/** 맞춰진 직후 C→M 획을 따라 지나가는 빛 */
const sweepGlow = (d: number) => (st: Sticker) => {
  if (d <= 0 || st.seq < 0) return 0;
  const x = d * 9 - st.seq * 0.6 - 1;
  return x > 0 && x < 2.4 ? 0.42 * Math.sin((x / 2.4) * Math.PI) : 0;
};

const ANIM: Record<BrandCubeAnim, BrandCubeAnimSpec> = {
  static: { T: 1, rest: 0, f: () => ({ cube: SOLVED }) },
  solve: {
    T: T_SOLVE,
    rest: T_SOLVED + 1.6,
    f: (t) => {
      const d = t - T_SOLVED;
      const alpha = Math.min(clamp(t / 0.35), clamp((T_SOLVE - t) / 0.35));
      const zoom = d > 0 ? 1 + 0.035 * Math.sin(Math.PI * clamp(d / 0.5)) : 1;
      return { cube: cubeAt(solveSeq, t), alpha, zoom };
    },
  },
  spin: { T: len(turnSeq), rest: 0, f: (t) => ({ cube: cubeAt(turnSeq, t) }) },
  loader: { T: len(loadSeq), rest: 0, f: (t) => ({ cube: cubeAt(loadSeq, t), zoom: 1.12 }) },
  gather: {
    T: 5.8,
    rest: 0,
    f: (t) => {
      const g0 = 1.7;
      const step = 0.075;
      const dur = 0.5;
      const gEnd = g0 + 25 * step + dur;
      const K0 = t < 0.5 ? 0 : t < 1.3 ? 1 - Math.pow(1 - (t - 0.5) / 0.8, 3) : 1;
      const drift = t >= 0.5 && t < gEnd ? Math.sin((t - 0.5) * 2.2) : 0;
      const kOf = (c: Cubie) => {
        if (t < g0) return K0;
        const p = clamp((t - g0 - GORDER[c.h.join()] * step) / dur);
        return 1 - easeSoft(p);
      };
      const left = t < g0 ? K0 : 1 - ease(clamp((t - g0) / (gEnd - g0)));
      return {
        cube: SOLVED,
        shadow: 1 - 0.35 * left,
        glow: sweepGlow(t - gEnd),
        xf: (c) => {
          const k = kOf(c);
          const r = RND(c.h);
          const kk = k * (1 + 0.08 * drift * r[3]);
          return {
            off: [0, 1, 2].map((i) => (c.h[i] + r[i] * 0.5) * 0.78 * kk) as Vec,
            R: mm(rot('x', r[0] * 1.1 * kk), rot('y', r[1] * 1.3 * kk)),
          };
        },
      };
    },
  },
  float: {
    T: 3.2,
    rest: 0,
    f: (t) => {
      const w = Math.sin((t / 3.2) * Math.PI * 2);
      return { cube: SOLVED, lift: -22 - 22 * w, shadow: 1 - (0.1 * (w + 1)) / 2 };
    },
  },
  assemble: {
    T: 3.6,
    rest: 3.0,
    f: (t) => {
      const k = t < 3.15 ? 1 : clamp(1 - (t - 3.15) / 0.35);
      return { cube: SOLVED, scale: (st) => (st.seq < 0 ? 1 : k * clamp(back(clamp((t - 0.15 - st.seq * 0.13) / 0.32)))) };
    },
  },
  shimmer: {
    T: 2.4,
    rest: 0,
    f: (t) => ({
      cube: SOLVED,
      glow: (st) => {
        if (st.seq < 0 || st.seq >= 7) return 0;
        const d = t * 5 - st.seq;
        return d > 0 && d < 2 ? 0.55 * Math.sin((d / 2) * Math.PI) : 0;
      },
    }),
  },
};
